import { useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/store/hooks";
import { navigate } from "../../../redux/slice/layoutSlice";
import { setSelected } from "../../../redux/slice/selectionSlice";
import {
  useGetUsersQuery,
  useGetOrganizationsQuery,
  useDeleteUserMutation,
} from "../../../redux/api";
import type { User } from "../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEdit,
  IconTrash,
} from "../../../components/icons";
import { RowMenu } from "../../../components/RowMenu";

const ENTITY = "owner";
const DETAIL_PATH = "/owner";
const COLUMN_COUNT = 6;

const statusTone = (status?: string | null): string => {
  switch ((status ?? "").toLowerCase()) {
    case "active":
      return "badge--success";
    case "invited":
    case "pending":
      return "badge--info";
    case "suspended":
      return "badge--warning";
    case "inactive":
    case "deleted":
      return "badge--danger";
    default:
      return "badge--info";
  }
};

const fullName = (u: User) => `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();

// Platform Owner → Owners. Manage the user accounts (reusing the user API);
// the platform owner can create owner accounts and grant org access.
const Owners = () => {
  const dispatch = useAppDispatch();

  const {
    data: users = [],
    isLoading: loadingUsers,
    isFetching,
    error,
    refetch,
  } = useGetUsersQuery();

  // Needed to know who an owner actually IS — see the note on `owners` below.
  const { data: organizations = [], isLoading: loadingOrganizations } =
    useGetOrganizationsQuery();

  const [deleteUser] = useDeleteUserMutation();

  const [search, setSearch] = useState("");

  // Both lists are required before the table can say anything true: with users
  // loaded but organizations still in flight, every row would be filtered out
  // and the screen would claim "No owners yet".
  const isLoading = loadingUsers || loadingOrganizations;

  /**
   * Who counts as an owner comes from the ORGANIZATION, never from the user's
   * membership roles.
   *
   * `users` is every account on the platform — for a platform owner the
   * resolver returns the lot, unfiltered (see the `users` query in
   * services' user/graphql.ts) — so rendering it directly listed every
   * employee, manager and admin of every organization as an "owner".
   *
   * `Organization.ownerEmail` is non-null server-side and is the only
   * trustworthy signal. Membership roles are not: `user-organizations`
   * routinely records an owner as `admin`, and an organization created at
   * signup writes its owner into the user document's embedded map and may
   * leave no membership row at all. That is the same trap mobile's People →
   * Owners fell into and was fixed for in 1.0.018.
   *
   * Matching is case-insensitive on both sides: the server lower-cases
   * `ownerEmail` on write, but the user records compared against it are not
   * normalised, so an owner who signed up with a capitalised address would
   * otherwise vanish from this list.
   */
  const owners = useMemo(() => {
    const ownerEmails = new Set(
      organizations
        .map((org) => (org.ownerEmail ?? "").trim().toLowerCase())
        .filter(Boolean),
    );
    if (ownerEmails.size === 0) return [];
    return users.filter((u: User) => ownerEmails.has((u.email ?? "").toLowerCase()));
  }, [users, organizations]);


  const openDetail = (id: string | null) => {
    dispatch(setSelected({ entity: ENTITY, id }));
    dispatch(navigate(DETAIL_PATH));
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Delete "${fullName(user) || user.email}"?`)) return;
    try {
      await deleteUser(user.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete user.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return owners;
    return owners.filter((u: User) =>
      [u.firstName, u.lastName, u.email, u.phone, u.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [owners, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load owners"
    : null;

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Owners</h2>
            <p className="panel__sub">
              Showing {visible.length} of {owners.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search owners…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New owner
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading owners…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {owners.length === 0
                      ? "No owners yet."
                      : "No owners match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((user: User) => (
                  <tr key={user.id}>
                    <td className="cell-date">
                      <span className="pill pill--neutral">{user.id}</span>
                    </td>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => openDetail(user.id)}
                      >
                        {fullName(user) || "—"}
                      </button>
                    </td>
                    <td className="cell-date">{user.email}</td>
                    <td>{user.phone ?? "—"}</td>
                    <td>
                      <span className={`badge ${statusTone(user.status)}`}>
                        <span className="badge__dot" />
                        {user.status ?? "unknown"}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "Edit owner",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(user.id),
                          },
                          {
                            label: "Delete owner",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(user),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
};

export default Owners;
